import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { FileText, Pencil, Image as ImageIcon, Upload } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Textarea } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { uploadSiteImage } from '@/lib/siteImageApi';
import { SITE_IMAGE_KEYS } from '@/constants/siteImageKeys';
import { ADMIN_PAGES } from '@/constants/adminPages';
import { humanizeKey, humanizeNamespace } from '@/lib/humanizeKey';

interface ContentRow {
  key: string;
  locale: string;
  value: string;
}

interface KeyEntry {
  key: string;
  namespace: string;
  en: string;
  ko: string;
  bn: string;
}

export default function AdminWebsitePage({ pageSlug = 'settings' }: { pageSlug?: string }) {
  const config = useMemo(() => ADMIN_PAGES.find((p) => p.slug === pageSlug), [pageSlug]);
  const { push } = useToast();

  const [rows, setRows] = useState<ContentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ en: '', ko: '', bn: '' });
  const [busy, setBusy] = useState(false);

  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [imagesLoading, setImagesLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingKeyRef = useRef<string | null>(null);

  const loadContent = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('rxk_site_content').select('key, locale, value');
    setRows((data as ContentRow[]) ?? []);
    setLoading(false);
  }, []);

  const loadImages = useCallback(async () => {
    setImagesLoading(true);
    const { data } = await supabase.from('rxk_site_images').select('key, url');
    const overrides: Record<string, string> = {};
    for (const row of (data as { key: string; url: string }[]) ?? []) overrides[row.key] = row.url;
    setImageUrls(overrides);
    setImagesLoading(false);
  }, []);

  useEffect(() => {
    loadContent();
    loadImages();
  }, [loadContent, loadImages]);

  const entries = useMemo<KeyEntry[]>(() => {
    const byKey = new Map<string, KeyEntry>();
    for (const r of rows) {
      const entry = byKey.get(r.key) ?? { key: r.key, namespace: r.key.split('.')[0] ?? r.key, en: '', ko: '', bn: '' };
      if (r.locale === 'en') entry.en = r.value;
      if (r.locale === 'ko') entry.ko = r.value;
      if (r.locale === 'bn') entry.bn = r.value;
      byKey.set(r.key, entry);
    }
    return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [rows]);

  const pageEntries = useMemo(() => {
    if (!config) return [];
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (!config.namespaces.includes(e.namespace)) return false;
      if (!q) return true;
      return e.key.toLowerCase().includes(q) || e.en.toLowerCase().includes(q) || e.ko.toLowerCase().includes(q) || e.bn.toLowerCase().includes(q);
    });
  }, [entries, config, search]);

  const pageImageDefs = useMemo(() => {
    if (!config) return [];
    return SITE_IMAGE_KEYS.filter((def) => config.namespaces.includes(def.key.split('.')[0]));
  }, [config]);

  // Grouped by namespace whenever a page spans more than one (mainly
  // Settings, which bundles a few small shared namespaces together) — a
  // single-namespace page just gets one unlabeled group.
  const groupedEntries = useMemo(() => {
    const groups = new Map<string, KeyEntry[]>();
    for (const e of pageEntries) {
      const list = groups.get(e.namespace) ?? [];
      list.push(e);
      groups.set(e.namespace, list);
    }
    return [...groups.entries()];
  }, [pageEntries]);
  const showGroupHeaders = (config?.namespaces.length ?? 0) > 1;

  const openEdit = (entry: KeyEntry) => {
    setEditingKey(entry.key);
    setEditForm({ en: entry.en, ko: entry.ko, bn: entry.bn });
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingKey) return;
    setBusy(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const upserts = (['en', 'ko', 'bn'] as const).map((locale) => ({
        key: editingKey,
        locale,
        value: editForm[locale],
        updated_by: user?.id ?? null,
      }));
      const { error } = await supabase.from('rxk_site_content').upsert(upserts, { onConflict: 'key,locale' });
      if (error) throw error;
      push('success', 'Content updated — live on the site immediately');
      setEditingKey(null);
      await loadContent();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const triggerUpload = (key: string) => {
    pendingKeyRef.current = key;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const key = pendingKeyRef.current;
    e.target.value = '';
    if (!file || !key) return;
    setUploadingKey(key);
    try {
      await uploadSiteImage(key, file);
      push('success', 'Image updated — live on the site immediately');
      await loadImages();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingKey(null);
    }
  };

  if (!config) {
    return <EmptyState title="Unknown page" />;
  }

  return (
    <>
      <div className="space-y-5">
        <div>
          <h1 className="text-[18px] font-bold text-ink">{config.title}</h1>
          <p className="text-[12px] text-ink-faint mt-0.5">Shared strings used across the site (common/cart/sticky bar)</p>
        </div>

        {pageImageDefs.length > 0 && (
          <Card>
            <CardHeader title="Images" subtitle={`${pageImageDefs.length} keys`} icon={<ImageIcon className="w-4 h-4" />} />
            <CardBody className="space-y-2">
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleFileChange} />
              {imagesLoading ? (
                <Spinner />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pageImageDefs.map((def) => (
                    <div key={def.key} className="border border-border-subtle rounded-lg p-3 flex flex-col gap-2">
                      <div className="h-28 rounded-lg bg-surface-muted flex items-center justify-center overflow-hidden">
                        <img src={imageUrls[def.key] ?? def.fallbackUrl} alt={def.label} className="max-h-full max-w-full object-contain" />
                      </div>
                      <p className="text-[12px] font-semibold text-ink">{def.label}</p>
                      <p className="text-[10px] text-ink-faint leading-snug">{def.recommendedSize}</p>
                      <Button size="sm" variant="secondary" onClick={() => triggerUpload(def.key)} disabled={uploadingKey === def.key}>
                        <Upload className="w-3.5 h-3.5" /> {uploadingKey === def.key ? 'Uploading…' : 'Replace'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader
            title="Text"
            subtitle={`${pageEntries.length} keys`}
            icon={<FileText className="w-4 h-4" />}
            action={
              <Field label="">
                <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="!w-56" />
              </Field>
            }
          />
          <CardBody className="space-y-3">
            {loading ? (
              <Spinner />
            ) : pageEntries.length === 0 ? (
              <EmptyState title="No matching keys" />
            ) : (
              groupedEntries.map(([namespace, list]) => (
                <div key={namespace}>
                  {showGroupHeaders && (
                    <p className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-ink-faint px-1 pb-1.5">
                      {humanizeNamespace(namespace)}
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {list.map((entry) => (
                      <button
                        key={entry.key}
                        onClick={() => openEdit(entry)}
                        className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg border border-border-subtle hover:bg-surface-muted hover:border-border transition-colors duration-150 text-left cursor-pointer"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-semibold text-ink truncate">{humanizeKey(entry.key)}</p>
                          <p className="text-[11px] text-ink-muted truncate">{entry.en || '(empty)'}</p>
                        </div>
                        <Pencil className="w-3 h-3 text-ink-faint flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={!!editingKey}
        onClose={() => setEditingKey(null)}
        title={editingKey ? humanizeKey(editingKey) : 'Edit Content'}
        subtitle={editingKey ?? undefined}
        icon={<Pencil className="w-3.5 h-3.5" />}
        busy={busy}
        footer={
          <>
            <Button type="button" variant="secondary" size="md" className="flex-1" onClick={() => setEditingKey(null)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" form="edit-page-content-form" size="md" className="flex-1" loading={busy}>
              Save
            </Button>
          </>
        }
      >
        <form id="edit-page-content-form" onSubmit={handleSave} className="space-y-3.5">
          <Field label="English">
            <Textarea value={editForm.en} onChange={(e) => setEditForm((f) => ({ ...f, en: e.target.value }))} rows={3} required />
          </Field>
          <Field label="한국어 (Korean)">
            <Textarea value={editForm.ko} onChange={(e) => setEditForm((f) => ({ ...f, ko: e.target.value }))} rows={3} required />
          </Field>
          <Field label="বাংলা (Bengali)">
            <Textarea value={editForm.bn} onChange={(e) => setEditForm((f) => ({ ...f, bn: e.target.value }))} rows={3} required />
          </Field>
        </form>
      </Modal>
    </>
  );
}
